import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { NOTE_ACTION_REGISTRY, getNoteRegistryRows } from "../../../../constants/noteActionRegistry";

const BUCKET_COLORS = {
  production: "warning",
  sales: "success",
  project: "primary",
  customer: "secondary",
  finance: "error",
  system: "default",
};

const formatLabel = (value = "") =>
  String(value || "")
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export default function NoteManagementDialog() {
  const [searchText, setSearchText] = useState("");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [productionOnly, setProductionOnly] = useState(false);

  const rows = useMemo(() => getNoteRegistryRows(), []);
  const scopeOptions = useMemo(
    () => Array.from(new Set(NOTE_ACTION_REGISTRY.map((row) => row.scope).filter(Boolean))).sort(),
    []
  );

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return rows.filter((row) => {
      if (bucketFilter !== "all" && row.bucket !== bucketFilter) return false;
      if (scopeFilter !== "all" && row.scope !== scopeFilter) return false;
      if (productionOnly && !row.appearsInProductionPopup) return false;
      if (!q) return true;

      const haystack = [
        row.action,
        row.label,
        row.description,
        row.bucket,
        row.scope,
        ...(row.visibleIn || []),
        ...(row.foundIn || []),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [bucketFilter, productionOnly, rows, scopeFilter, searchText]);

  const bucketCounts = useMemo(() => {
    return filteredRows.reduce((acc, row) => {
      acc[row.bucket] = (acc[row.bucket] || 0) + 1;
      return acc;
    }, {});
  }, [filteredRows]);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Shows only note actions that are actually created by application code. Production includes purchasing,
        shipping, proofs, supplier communication, PO tasks, tracking, and receiving.
      </Typography>

      <Alert severity="info" sx={{ alignItems: "flex-start" }}>
        This registry is source-audited. It intentionally excludes guessed, planned, alias, and legacy rows unless
        that exact action string is written by the code today.
      </Alert>

      <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
        Production popup only shows production bucket actions. General project notes, customer notes, sales document
        notes, finance notes, and system notes stay out of that popup unless the stored action is truly production.
      </Alert>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {["production", "sales", "project", "customer", "finance", "system"].map((bucket) => (
          <Chip
            key={bucket}
            color={BUCKET_COLORS[bucket] || "default"}
            label={`${formatLabel(bucket)}: ${bucketCounts[bucket] || 0}`}
            variant={bucketCounts[bucket] ? "filled" : "outlined"}
          />
        ))}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr auto" },
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          fullWidth
          label="Search"
          placeholder="Search by action, label, description, or file path"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel id="note-management-bucket-label">Bucket</InputLabel>
          <Select
            labelId="note-management-bucket-label"
            value={bucketFilter}
            label="Bucket"
            onChange={(event) => setBucketFilter(event.target.value)}
          >
            <MenuItem value="all">All buckets</MenuItem>
            <MenuItem value="production">Production</MenuItem>
            <MenuItem value="sales">Sales</MenuItem>
            <MenuItem value="project">Project</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="finance">Finance</MenuItem>
            <MenuItem value="system">System</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="note-management-scope-label">Scope</InputLabel>
          <Select
            labelId="note-management-scope-label"
            value={scopeFilter}
            label="Scope"
            onChange={(event) => setScopeFilter(event.target.value)}
          >
            <MenuItem value="all">All scopes</MenuItem>
            {scopeOptions.map((scope) => (
              <MenuItem key={scope} value={scope}>
                {formatLabel(scope)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
          <Typography variant="body2" color="text.secondary">
            Production popup only
          </Typography>
          <Switch checked={productionOnly} onChange={(event) => setProductionOnly(event.target.checked)} />
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "65vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Bucket</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Visible In</TableCell>
              <TableCell>Production Popup</TableCell>
              <TableCell>Project Notes</TableCell>
              <TableCell>Customer Notes</TableCell>
              <TableCell>Finance</TableCell>
              <TableCell>Found In</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow
                key={row.action}
                hover
                sx={{ backgroundColor: row.bucket === "production" ? "rgba(237, 108, 2, 0.08)" : "inherit" }}
              >
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {row.action}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {row.label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={BUCKET_COLORS[row.bucket] || "default"} label={formatLabel(row.bucket)} />
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={formatLabel(row.scope)} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {row.visibleIn.map((area) => (
                      <Chip key={area} size="small" variant="outlined" label={formatLabel(area)} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>{row.appearsInProductionPopup ? "Yes" : "No"}</TableCell>
                <TableCell>{row.appearsInProjectNotes ? "Yes" : "No"}</TableCell>
                <TableCell>{row.appearsInCustomerNotes ? "Yes" : "No"}</TableCell>
                <TableCell>{row.appearsInFinance ? "Yes" : "No"}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {row.foundIn.map((filePath) => (
                      <Typography key={filePath} variant="caption" sx={{ fontFamily: "monospace" }}>
                        {filePath}
                      </Typography>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.description}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {!filteredRows.length && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary">
                    No note actions match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
